/**
 * useAuth Hook
 *
 * Provides a convenient interface to auth state and actions.
 * Sets up auth initialization on mount.
 */

import { useEffect } from 'react';
import type { AuthUser } from '../../../types/electron';
import { useAuthStore, type SignUpResult } from '../store/authStore';
import { isAuthConfigured } from '../services/authService';
import type { OAuthProvider } from '../services/authService';

export interface UseAuthReturn {
  /** Current authenticated user */
  user: AuthUser | null;
  /** Whether auth is loading */
  isLoading: boolean;
  /** Whether auth has been initialized */
  isInitialized: boolean;
  /** Current error message */
  error?: string;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether auth backend is configured */
  isConfigured: boolean;

  // Actions
  signUp: (email: string, password: string) => Promise<SignUpResult>;
  signIn: (email: string, password: string) => Promise<boolean>;
  signInWithOAuth: (provider: OAuthProvider) => Promise<boolean>;
  signOut: () => Promise<boolean>;
  clearError: () => void;
}

/**
 * Hook for managing authentication state and actions.
 */
export function useAuth(): UseAuthReturn {
  const store = useAuthStore();

  // Initialize auth on mount
  useEffect(() => {
    store.initialize();
  }, []);

  return {
    user: store.user,
    isLoading: store.isLoading,
    isInitialized: store.isInitialized,
    error: store.error,
    isAuthenticated: Boolean(store.user),
    isConfigured: isAuthConfigured(),

    signUp: store.signUp,
    signIn: store.signIn,
    signInWithOAuth: store.signInWithOAuth,
    signOut: store.signOut,
    clearError: store.clearError,
  };
}

export type { OAuthProvider, SignUpResult };
